import { useClientStore } from '@/features/Clients';
import type {
  ContractRequestCalculation,
  ContractRequestConfiguration,
  ContractRequestData,
} from '@/features/Contract/types';
import { useGlobalStore } from '@/features/Global/store';
import { useQuoteStore } from '@/features/Quotes/store';
import type { InventoryContract } from '@/features/Quotes/types';
import { useContractStore } from '@/features/Sparten/';
import { useTariffStore } from '@/features/Tariffs/store';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { VglError } from '@/services/errorHandler/vglError';
import { transformDates } from '@/utils/dates/transformDates';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';

import { RadioGroupOption } from '@/components/form-elements/RadioGroup/types';
import { useCompanyQuestionStore } from '@/features/CompanyQuestions/store';
import {
  COMPANY_QUESTION_COMPONENT,
  type CompanyQuestionSelectionOption,
} from '@/features/CompanyQuestions/types';
import { useDistributionSelection } from '@/features/Distribution/useDistributionSelection';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { useEntityManager } from '@/features/EntityManager/useEntityManager';
import { buildFeatureObject } from '@/features/Global/searchDataUtils';

export const enrichInputStateWithContent = (
  inputState: Record<string, unknown>,
): Record<string, unknown> => {
  const store = useCompanyQuestionStore();
  const enrichedState: Record<string, unknown> = { ...inputState };

  const questionMap = new Map<string, (typeof store.data)[0]['questions'][0]>();
  store.data.forEach((group) => {
    group.questions.forEach((question) => {
      questionMap.set(question.fieldName, question);
    });
  });

  Object.entries(inputState).forEach(([key, value]) => {
    const question = questionMap.get(key);
    if (!question) {
      return;
    }

    let label: string | undefined;

    const getOptionValue = (
      options: CompanyQuestionSelectionOption[] | RadioGroupOption[],
      value: unknown,
    ) => {
      return options.find((opt) => String(opt.value) === String(value))?.label;
    };

    if (
      question.component === COMPANY_QUESTION_COMPONENT.SELECT &&
      question.selectOptions.length > 0
    ) {
      label = getOptionValue(question.selectOptions, value);
    } else if (
      question.component === COMPANY_QUESTION_COMPONENT.RADIO &&
      question.radioOptions.length > 0
    ) {
      label = getOptionValue(question.radioOptions, value);
    }

    if (label !== undefined) {
      enrichedState[`${key}_content`] = label;
    } else {
      enrichedState[`${key}_content`] = value;
    }
  });

  return enrichedState;
};

export const buildContractData = (): ContractRequestData['antragsdaten'] => ({
  ...getCurrentSpartenConfig().buildContractDataObject(
    transformDates(useContractStore().$state as Record<string, unknown>),
  ),
  gesellschaftsspezifischeFragen:
    useCompanyQuestionStore().relevantQuestionPaths,
  gesellschaftsspezifischeAntworten: transformDates(
    enrichInputStateWithContent(
      useCompanyQuestionStore().getDehydratedInputState(),
    ),
  ),
});

export const buildInputData = (): ContractRequestData['eingabedaten'] =>
  useGlobalStore().getTariffSearchData(true).eingabedaten;

export const buildCalculationObj = (): ContractRequestCalculation => {
  const tariffStore = useTariffStore();

  return {
    id: tariffStore.calculationId,
    empfohlenderTarifId: tariffStore.recommendation ?? '',
    filters: tariffStore.filters,
    tarife: tariffStore.selectedTariffs.map((tariff) => ({
      // fall back to company ID when pooling (do we need this?)
      distributionInsuranceId:
        useDistributionSelection(tariff.gesellschaft.id).selectedChannelId
          .value ?? tariff.gesellschaft.id,
      // Is not relevant here as this does only exist in BU, RLV and PKV
      dokumente: [],
      id: tariff.identifier,
      name: tariff.name,
    })),
  };
};

export const buildConfiguration = (): ContractRequestConfiguration => {
  const tariffStore = useTariffStore();
  const tariff = tariffStore.contractTariff;
  const { selectedPoolId } = useDistributionSelection(tariff.gesellschaft.id);
  return {
    direktvereinbarung: tariffStore.filters.directAgreement,
    pool: selectedPoolId.value,
  };
};

export const buildContractRequestData = (): ContractRequestData => {
  const clientStore = useClientStore();
  const { id } = clientStore.versicherungsnehmer.data;
  if (id === null) {
    throw new VglError(VGL_ERROR_CODE.CONTRACT_VN_HAS_NO_ID, {
      additionalErrorMessage: 'Error: id is null',
    });
  }

  const { brokerIdForSelectedDistribution } = useDistributionSelection(
    useTariffStore().contractTariff.gesellschaft.id,
  );

  return {
    antragsdaten: buildContractData(),
    berechnung: buildCalculationObj(),
    eingabedaten: buildInputData(),
    entities: useEntityManager().prepareEntitiesForBackendRequest([
      ENTITY_TYPE.CLIENT,
    ]),
    konfigurationsParameter: buildConfiguration(),
    kundenId: id,
    brokerId: brokerIdForSelectedDistribution.value,
    merkmale: buildFeatureObject(),
  };
};

export const buildInventoryContract = (
  contractData: ContractRequestData,
): InventoryContract => {
  let title = '';
  const config = getCurrentSpartenConfig();

  if (config.inventoryContractTitle !== undefined) {
    title = config.inventoryContractTitle();
  }

  return {
    title,
    clientId: contractData.kundenId,
    content: {
      ...useQuoteStore().buildContent(),
      contractData: contractData.antragsdaten,
    },
    tariffId: useTariffStore().contractTariff.identifier,
  };
};
